import type { FeatureToggles } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type { ShortCircuitScenarioElementCalculation } from '../../..';
import type { ForFaultsEvaluation } from '../../../evaluation/shortCircuit/ForFaultsEvaluation';
import { evaluateProtectingReleaseTime } from '../../../evaluation/shortCircuit/singleElement/evaluateGridElement';
import { FaultEvaluation } from '../../../evaluation/shortCircuit/singleElement/faultEvaluation';
import type { FunctionalGrid } from '../../../grid/FunctionalGrid';
import { FunctionalGridGraph } from '../../../grid/FunctionalGridGraph';
import {
  findGridElementLabel,
  findGridElementTitle
} from '../findGridElementLabel';
import type { PowerTransformerShortCircuitDetails } from './ShortCircuitDetails';
import { Features } from '../../../createGridElementCalculationSetExecutionDetails';

export function createPowerTransformersShortCircuitDetails(
  functionalGrid: FunctionalGrid,
  features: Features,
  evaluationEngine: ForFaultsEvaluation,
  shortCircuitScenarioElements: ShortCircuitScenarioElementCalculation[],
  featureToggles?: FeatureToggles
): PowerTransformerShortCircuitDetails[] {
  return shortCircuitScenarioElements
    .filter(gridElement => gridElement.objectType === 'PowerTransformer')
    .map(gridElement =>
      createPowerTransformerShortCircuitDetails(
        gridElement,
        features,
        functionalGrid,
        evaluationEngine,
        shortCircuitScenarioElements,
        featureToggles
      )
    );
}
function createPowerTransformerShortCircuitDetails(
  gridElement: ShortCircuitScenarioElementCalculation,
  features: Features,
  functionalGrid: FunctionalGrid,
  evaluationEngine: ForFaultsEvaluation,
  shortCircuitScenarioElements: ShortCircuitScenarioElementCalculation[],
  featureToggles?: FeatureToggles
): PowerTransformerShortCircuitDetails {
  const ends = getEnds(
    { id: gridElement.id, objectType: 'PowerTransformer' },
    functionalGrid
  );

  const functionalGridGraph = new FunctionalGridGraph(functionalGrid);
  const foundObject = functionalGridGraph.deepFirstSearchFromEdge(
    ends.primaryEnd,
    'Busbar',
    'PowerTransformer'
  );

  const mvBusbarScenarioElement = shortCircuitScenarioElements.find(
    e => e.id === foundObject?.objectId
  );

  if (mvBusbarScenarioElement !== undefined) {
    const faultEvaluation = FaultEvaluation.create(
      mvBusbarScenarioElement,
      evaluationEngine
    );

    return {
      id: gridElement.id,
      objectType: 'PowerTransformer',
      label: findGridElementLabel(features, gridElement.id),
      title: findGridElementTitle(features, gridElement.id),
      results: {
        protectingReleaseTime: evaluateProtectingReleaseTime(
          gridElement.result.releaseTime,
          !featureToggles?.isProtectiveDevicesEnabled
        ),
        phase1Min: faultEvaluation.onePhaseToGroundMin(),
        phase1Max: faultEvaluation.onePhaseToGroundMax(),
        phase2Min: faultEvaluation.twoPhasesMin(),
        phase3Max: faultEvaluation.threePhasesMax()
      }
    };
  } else {
    return {
      id: gridElement.id,
      objectType: 'PowerTransformer',
      label: findGridElementLabel(features, gridElement.id),
      title: findGridElementTitle(features, gridElement.id),
      results: {
        protectingReleaseTime: { type: 'CALCULATION_RESULT_NOT_AVAILABLE' },
        phase1Min: { type: 'CALCULATION_RESULT_NOT_AVAILABLE' },
        phase1Max: { type: 'CALCULATION_RESULT_NOT_AVAILABLE' },
        phase2Min: { type: 'CALCULATION_RESULT_NOT_AVAILABLE' },
        phase3Max: { type: 'CALCULATION_RESULT_NOT_AVAILABLE' }
      }
    };
  }
}

function getEnds(
  evaluatedPowerTransformer: {
    id: `{${string}}`;
    objectType: 'PowerTransformer';
  },
  functionalGrid: FunctionalGrid
) {
  const powerTransformerId = evaluatedPowerTransformer.id;
  const powerTransformerEnds = functionalGrid.powerTransformers.find(
    p => p.objectId === powerTransformerId
  )?.powerTransformerEnds;

  const primaryEnd = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 1 && powerTransformerEnds?.includes(p.objectId)
  );
  const secondaryEnd = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 2 && powerTransformerEnds?.includes(p.objectId)
  );
  const tertiaryEnd = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 3 && powerTransformerEnds?.includes(p.objectId)
  );
  if (primaryEnd === undefined || secondaryEnd === undefined) {
    throw new Error(
      `Primary  results not found for transformer ${powerTransformerId}`
    );
  }
  return { primaryEnd, secondaryEnd, tertiaryEnd };
}
